import { type ChangeEvent, type FormEvent, type SubmitEvent as ReactSubmitEvent, memo, useState } from "react";
import "Components/ConfigLayout/Camera/MotionSettings/MotionSettings.less";
import {
	AspectMaintainer, Button, ButtonTypes, InteractiveSvg, type SelectOption, Select, Card, SettingsLayout
} from "@clintonelec/react-storybook";
import ActionButtons from "Components/ConfigLayout/ActionButtons";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
import { selectMotionSettings, setMotionSettingsAction } from "Data/Redux/Slices/Settings/Camera/Motion";
import { produce } from "immer";
import { compact, constant, isEqual, times } from "lodash";
import VideoMotionControls from "Components/ConfigLayout/Camera/MotionSettings/VideoMotionControls";
import SmartMotionControls from "Components/ConfigLayout/Camera/MotionSettings/SmartMotionControls";
import { MotionMode, MotionObject } from "Data/Objects/Camera";
import type { Point } from "@clintonelec/react-storybook";

type MotionSettingsForm = {
	bikeConfidence: HTMLInputElement;
	bikeConfidenceLevel: HTMLInputElement;
	daySensitivityLevel: HTMLInputElement;
	motionMode: HTMLInputElement;
	nightSensitivityLevel: HTMLInputElement;
	personConfidence: HTMLInputElement;
	personConfidenceLevel: HTMLInputElement;
	vehicleConfidence: HTMLInputElement;
	vehicleConfidenceLevel: HTMLInputElement;
	zones: HTMLInputElement[][];
} & HTMLFormElement;

const motionModeOptions: SelectOption[] = [
	{
		label: "Video Motion",
		value: MotionMode.VIDEO
	},
	{
		label: "Smart Motion",
		value: MotionMode.SMART
	}
];

const defaultZonePoints = [
	{ x: 760, y: 340 },
	{ x: 1160, y: 340 },
	{ x: 1160, y: 740 },
	{ x: 760, y: 740 }
];

function MotionSettings() {
	const dispatch = useAppDispatch();
	const motionSettings = useAppSelector(selectMotionSettings);
	const [ localMotionSettings, setLocalMotionSettings ] = useState(motionSettings);
	const [ selectedZone, setSelectedZone ] = useState<number | null>(null);
	const setMotionSettings = () => dispatch(setMotionSettingsAction(localMotionSettings));
	const actionButtonDisabled = isEqual(motionSettings, localMotionSettings);
	const allZonesEmpty = !localMotionSettings.zones.some(zone => !!zone);

	const handleFormReset = (event: FormEvent<MotionSettingsForm>) => {
		event.preventDefault();

		setLocalMotionSettings(motionSettings);
		setSelectedZone(null);
	};

	const handleFormSubmit = (event: ReactSubmitEvent<MotionSettingsForm>) => {
		event.preventDefault();

		if (event.currentTarget?.checkValidity()) {
			setMotionSettings();
			setSelectedZone(null);
		}
	};

	const handleFormChange = (event: ChangeEvent<MotionSettingsForm>) => {
		const { personConfidence, vehicleConfidence, bikeConfidence } = event.currentTarget;
		const newLocalSettings = produce(localMotionSettings, draft => {
			draft.selectedMotionObjects = compact([
				personConfidence.checked ? MotionObject.PERSON : null,
				vehicleConfidence.checked ? MotionObject.VEHICLE : null,
				bikeConfidence.checked ? MotionObject.BIKE : null
			]);
		});

		setLocalMotionSettings(newLocalSettings);
	};

	const handleClearActiveZone = () => {
		setLocalMotionSettings(produce(localMotionSettings, draft => {
			if (selectedZone === null) {
				return;
			}

			draft.zones[ selectedZone ] = null;
		}));

		setSelectedZone(null);
	};

	const handleClearAllZones = () => {
		setLocalMotionSettings(produce(localMotionSettings, draft => {
			draft.zones = times(8, (constant(null)));
		}));

		setSelectedZone(null);
	};

	const handleMotionModeSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		const newLocalState = produce(localMotionSettings, draft => {
			draft.motionMode = value as MotionMode;
		});

		setLocalMotionSettings(newLocalState);
	};

	const handlePointsUpdate = (newPoints: Point[]) => {
		const newLocalState = produce(localMotionSettings, draft => {
			if (selectedZone === null) {
				return;
			}

			draft.zones[ selectedZone ] = newPoints;
		});

		setLocalMotionSettings(newLocalState);
	};

	const handleZoneButtonClick = (zoneIndex: number) => () => {
		const newIndex = selectedZone === zoneIndex ? null : zoneIndex;

		setSelectedZone(newIndex);

		if(newIndex !== null && localMotionSettings.zones[ newIndex ]?.length === 0) {
			const newLocalState = produce(localMotionSettings, draft => {
				draft.zones[ newIndex ] = defaultZonePoints;
			});

			setLocalMotionSettings(newLocalState);
		}
	};

	const renderInteractivePolygon = () => {
		if (selectedZone === null) {
			return;
		}

		return (
			<InteractiveSvg>
				<InteractiveSvg.Polygon
					key={ `zone-${ selectedZone }` }
					points={ localMotionSettings.zones[ selectedZone ] ?? [] }
					onChange={ handlePointsUpdate } />
			</InteractiveSvg>
		);
	};

	const handleCreateZoneClick = (index: number) => () => {
		setSelectedZone(index);
		setLocalMotionSettings(produce(localMotionSettings, (draft) => {
			draft.zones[ index ] = defaultZonePoints;
		}));
	};

	const renderZoneButtons = () => times(4, zoneIndex => {
		const selectedButton = selectedZone === zoneIndex;

		if (!localMotionSettings?.zones[ zoneIndex ]) {
			return (
				<Button
					key={ zoneIndex }
					className="zone-button"
					ghost={ !selectedButton }
					htmlType="button"
					onClick={ handleCreateZoneClick(zoneIndex) }
					type={ ButtonTypes.SECONDARY }
				>
					+
				</Button>
			);
		}

		return (
			<Button
				key={ zoneIndex }
				className="zone-button"
				ghost={ !selectedButton }
				htmlType="button"
				onClick={ handleZoneButtonClick(zoneIndex) }
				type={ !selectedButton ? ButtonTypes.SECONDARY : ButtonTypes.TERTIARY }
			>
				{ `Zone ${ zoneIndex + 1 }` }
			</Button>
		);
	});

	return (
		<SettingsLayout.Form
			className="motion-settings"
			onChange={ handleFormChange }
			onReset={ handleFormReset }
			onSubmit={ handleFormSubmit }
		>
			<SettingsLayout.ScrollableContainer>
				<Card size="large" title="Motion Settings">
					<div className="two-column-card-content">
						<div className="left-content">
							<div className="camera-container">
								<AspectMaintainer>
									<div className="camera-snapshot">
										{ renderInteractivePolygon() }
									</div>
								</AspectMaintainer>
							</div>
							<div className="controls-container">
								<div className="zone-buttons">
									{ renderZoneButtons() }
								</div>
								<Button
									className="zone-action-button"
									disabled={ selectedZone == null }
									ghost
									htmlType="button"
									onClick={ handleClearActiveZone }
									type={ ButtonTypes.SECONDARY }
								>
									Clear Active Zone
								</Button>
								<Button
									className="zone-action-button"
									disabled={ allZonesEmpty }
									ghost
									htmlType="button"
									onClick={ handleClearAllZones }
									type={ ButtonTypes.SECONDARY }
								>
									Clear All Zones
								</Button>
							</div>
						</div>
						<div className="right-content">
							<div className="motion-mode-settings">
								<div className="motion-setting-row">
									<span>Motion Mode</span>
									<Select
										allowClear={ false }
										className="motion-mode-select"
										onSelect={ handleMotionModeSelect }
										options={ motionModeOptions }
										value={ localMotionSettings.motionMode }
									/>
								</div>
								<SmartMotionControls
									localMotionSettings={ localMotionSettings }
									setLocalMotionSettings={ setLocalMotionSettings }
								/>
								<VideoMotionControls
									localMotionSettings={ localMotionSettings }
									setLocalMotionSettings={ setLocalMotionSettings }
								/>
							</div>
						</div>
					</div>
				</Card>
			</SettingsLayout.ScrollableContainer>
			<ActionButtons disabled={ actionButtonDisabled } />
		</SettingsLayout.Form>
	);
}

export default memo(MotionSettings);
